import React, { FC, memo, useCallback, useEffect, useMemo } from 'react';

import { withProviders } from 'application/helpers';
import PlayerProvider from 'application/providers/PlayerProvider';

import { DATA_TEST_IDS } from '../consts';
import { TEXTS } from '../texts';
import { PROMO_SELECTOR_LAYOUT } from '../types';
import PromoSelectTrigger from './PromoSelectTrigger';
import PromoSelectorDropdown from './PromoSelectorDropdown';
import type { PromoFieldShellProps } from './types';
import { usePromoFieldShellViewModel } from './usePromoFieldShellViewModel';

import styles from './index.module.scss';

const PromoFieldShell: FC<PromoFieldShellProps> = ({
  draftError,
  draftId,
  isDraftLoading,
  onDropdownLayoutChange,
  onDropdownOpenChange,
  onRetryDraftCreate,
  selectedCount,
  setSelectedCount,
  totalCount,
}) => {
  const {
    containerRef,
    triggerRef,
    dropdownId,
    dropdownState,
    filterButtonRef,
    filterContainerRef,
    hasNextPage,
    hasLoadedFirstPage,
    isDropdownOpen,
    isFilterOpen,
    isInitialLoading,
    isNextPageLoading,
    isBulkSelectionActionPending,
    isSelectionActionPending,
    items,
    queryError,
    queryKey,
    search,
    selectedStatuses,
    sort,
    statusesInfo,
    onDropdownToggle,
    onFilterToggle,
    onLoadMore,
    onSearchChange,
    onPromoPlaybackToggle,
    onPromoSelectionChange,
    onReset,
    onSelectAll,
    onRetry,
    onSortToggle,
    onStatusChange,
  } = usePromoFieldShellViewModel({
    draftError,
    draftId,
    isDraftLoading,
    onRetryDraftCreate,
    selectedCount,
    setSelectedCount,
    totalCount,
  });

  const promoSelectorLayout = useMemo(() => {
    if (!hasLoadedFirstPage || isDraftLoading || isInitialLoading || queryError || hasNextPage || items.length > 1) {
      return PROMO_SELECTOR_LAYOUT.EXPANDED;
    }

    return items.length === 1 ? PROMO_SELECTOR_LAYOUT.SINGLE_RESULT : PROMO_SELECTOR_LAYOUT.EMPTY;
  }, [hasLoadedFirstPage, hasNextPage, isDraftLoading, isInitialLoading, items.length, queryError]);
  const shouldUseCompactDropdownHeight = promoSelectorLayout !== PROMO_SELECTOR_LAYOUT.EXPANDED;
  const dropdownStyle = useMemo(
    () => ({
      position: 'fixed' as const,
      maxHeight: dropdownState.maxHeight,
      ...(shouldUseCompactDropdownHeight ? {} : { height: dropdownState.maxHeight }),
      top: dropdownState.fixedPosition?.top,
      left: dropdownState.fixedPosition?.left,
      width: dropdownState.fixedPosition?.width,
    }),
    [
      dropdownState.fixedPosition?.left,
      dropdownState.fixedPosition?.top,
      dropdownState.fixedPosition?.width,
      dropdownState.maxHeight,
      shouldUseCompactDropdownHeight,
    ]
  );

  useEffect(() => {
    if (!isDropdownOpen) {
      onDropdownOpenChange?.(false);
    }
  }, [isDropdownOpen, onDropdownOpenChange]);

  useEffect(() => {
    onDropdownLayoutChange?.(isDropdownOpen ? promoSelectorLayout : PROMO_SELECTOR_LAYOUT.EXPANDED);
  }, [isDropdownOpen, onDropdownLayoutChange, promoSelectorLayout]);

  useEffect(() => () => onDropdownOpenChange?.(false), [onDropdownOpenChange]);

  const onPromoDropdownToggle = useCallback(() => {
    // The modal height and the fixed dropdown position must be calculated in
    // the same React commit. Reporting this state from an effect leaves the
    // dropdown anchored to the pre-expansion modal layout.
    onDropdownOpenChange?.(!isDropdownOpen);
    onDropdownLayoutChange?.(PROMO_SELECTOR_LAYOUT.EXPANDED);
    onDropdownToggle();
  }, [isDropdownOpen, onDropdownLayoutChange, onDropdownOpenChange, onDropdownToggle]);

  return (
    <div
      className={styles.container}
      data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_SHELL}
      ref={containerRef}>
      <span
        id={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_LABEL}
        className={styles.label}
        data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_LABEL}>
        {TEXTS.AUDIO_REPORT_PROMOS_LABEL}
      </span>

      <PromoSelectTrigger
        ref={triggerRef}
        dropdownId={dropdownId}
        isOpen={isDropdownOpen}
        selectedCount={selectedCount}
        totalCount={totalCount}
        onClick={onPromoDropdownToggle}
      />

      {isDropdownOpen && (
        <PromoSelectorDropdown
          dropdownId={dropdownId}
          dropdownStyle={dropdownStyle}
          filterButtonRef={filterButtonRef}
          filterContainerRef={filterContainerRef}
          hasNextPage={hasNextPage}
          isFilterOpen={isFilterOpen}
          isInitialLoading={isDraftLoading || isInitialLoading}
          isNextPageLoading={isNextPageLoading}
          isBulkSelectionActionPending={isBulkSelectionActionPending}
          isSelectionActionPending={isSelectionActionPending}
          items={items}
          queryError={queryError}
          queryKey={queryKey}
          search={search}
          selectedCount={selectedCount}
          selectedStatuses={selectedStatuses}
          sort={sort}
          statusesInfo={statusesInfo}
          totalCount={totalCount}
          onFilterToggle={onFilterToggle}
          onLoadMore={onLoadMore}
          onSearchChange={onSearchChange}
          onPromoPlaybackToggle={onPromoPlaybackToggle}
          onPromoSelectionChange={onPromoSelectionChange}
          onReset={onReset}
          onSelectAll={onSelectAll}
          onRetry={onRetry}
          onSortToggle={onSortToggle}
          onStatusChange={onStatusChange}
        />
      )}
    </div>
  );
};

export { PromoFieldShell };

export default memo(withProviders<PromoFieldShellProps>(PromoFieldShell, [PlayerProvider]));
